import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { EditorModal } from "@/components/EditorModal";
import { StaffEvaluationRequestForm } from "@/components/StaffEvaluationRequestForm";
import { api, formatApiError } from "@/lib/api";
import { StageBar } from "@/components/pipeline/StageBar";
import { PipelineDetail } from "@/components/pipeline/PipelineDetail";
import { ReportsTab } from "@/components/pipeline/ReportsTab";

const day = (value) => (value ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "No due date");

const Row = ({ row, active, onOpen }) => (
  <article className={`pipeline-row ${active ? "active" : ""} ${row.days_late > 0 ? "late" : ""} ${row.status === "On hold" ? "on-hold" : ""}`} data-testid={`pipeline-row-${row.id}`} onClick={() => onOpen(row)}>
    <div className="pipeline-cell student">
      <strong>{row.student_initials}</strong>
      <span>{row.client_name}{row.grade ? ` · Gr ${row.grade}` : ""}</span>
    </div>
    <div className="pipeline-cell service">
      <strong data-testid={`pipeline-service-${row.id}`}>{row.service_label}</strong>
      <span className={`rate-chip ${row.rate.status === "confirmed" ? "ok" : "warn"}`}>{row.rate.status === "confirmed" ? "Rate confirmed" : row.rate.hint}</span>
    </div>
    <div className="pipeline-cell provider"><span>{row.provider_name || "Unassigned"}</span></div>
    <div className="pipeline-cell stage">
      <StageBar compact stageIndex={row.stage_index} stage={row.stage} testId={`pipeline-stage-${row.id}`} />
      <span>{row.stage === "Closed" || row.stage === "On hold" ? `${row.stage}${row.stage_note ? ` · ${row.stage_note}` : ""}` : `${row.stage}${row.stage_note ? ` · ${row.stage_note}` : ""}`}</span>
    </div>
    <div className="pipeline-cell meeting" data-testid={`pipeline-meeting-${row.id}`}>
      <strong>{row.meeting_date ? day(row.meeting_date) : "—"}</strong>
      <span>{row.meeting_date ? (row.due_date_source === "override" ? "Due date overridden" : "Report due 7 days before") : "No meeting date"}</span>
    </div>
    <div className={`pipeline-cell due ${row.days_late > 0 ? "late" : ""}`}>
      <strong>{day(row.due_date)}</strong>
      <span>{row.days_late > 0 ? `${row.days_late} d late` : row.stage === "Closed" ? "Closed" : row.stage === "On hold" ? "Paused" : "On time"}</span>
    </div>
    <div className="pipeline-cell action">
      {row.next_step && (
        <button className={row.next_step.primary ? "primary-button small" : "ghost-button small"} data-testid={`pipeline-next-${row.id}`} type="button" onClick={(event) => { event.stopPropagation(); onOpen(row); }}>
          {row.next_step.label}
        </button>
      )}
    </div>
  </article>
);

export default function EvaluationPipelinePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get("tab") || "needs_you");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ client_id: "", provider_id: "", discipline: "" });
  const [selected, setSelected] = useState(null);
  const [showNewForm, setShowNewForm] = useState(() => searchParams.get("new") === "1");
  const [clientOptions, setClientOptions] = useState([]);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    api.get("/requests/client-options").then((response) => setClientOptions(response.data)).catch(() => setClientOptions([]));
  }, []);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/evaluation-pipeline", { params: { q, ...filters } });
      setData(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [q, filters]);

  useEffect(() => {
    const handle = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(handle);
  }, [load, q]);

  const items = useMemo(() => (data?.items || []).filter((row) => row.tabs.includes(tab)), [data, tab]);
  const current = selected ? (data?.items || []).find((row) => row.id === selected.id) || null : null;

  return (
    <div className="app-shell" data-testid="evaluation-pipeline-shell">
      <AppSidebar activeItem="Evaluation pipeline" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page evaluation-pipeline-page" data-testid="evaluation-pipeline-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">ADMIN · EVALUATIONS</p>
              <h1>Evaluation pipeline</h1>
              <p className="page-subtitle">Every evaluation, screening and other service, one row each, with the next step you need to take.</p>
            </div>
            <button className="primary-button" data-testid="pipeline-new-request" type="button" onClick={() => setShowNewForm(true)}><Plus size={16} /> New request</button>
          </div>
          {showNewForm && (
            <EditorModal onClose={() => { setShowNewForm(false); if (searchParams.get("new")) setSearchParams({}); }} testId="staff-evaluation-modal">
              <StaffEvaluationRequestForm
                clientOptions={clientOptions}
                onClose={() => { setShowNewForm(false); if (searchParams.get("new")) setSearchParams({}); }}
                onCreated={(created) => {
                  setShowNewForm(false);
                  if (searchParams.get("new")) setSearchParams({});
                  setNotice(`${created.service_kind || "Request"} for ${created.student_name} (${created.client_name}) logged${created.linked_ids?.length ? ` with ${created.linked_ids.length} linked request${created.linked_ids.length === 1 ? "" : "s"}` : ""}.`);
                  load();
                }}
              />
            </EditorModal>
          )}
          {notice && <p className="form-message" data-testid="pipeline-notice">{notice}</p>}
          {error && <p className="form-message error-message" data-testid="pipeline-error">{error}</p>}
          <div className="pipeline-tabs" role="tablist" data-testid="pipeline-tabs">
            {(data?.tabs || []).map((item) => (
              <button key={item.key} role="tab" aria-selected={tab === item.key} className={`pipeline-tab ${tab === item.key ? "active" : ""}`} data-testid={`pipeline-tab-${item.key}`} type="button" onClick={() => setTab(item.key)}>
                {item.label} <em data-testid={`pipeline-count-${item.key}`}>{item.count}</em>
              </button>
            ))}
            <button role="tab" aria-selected={tab === "reports"} className={`pipeline-tab ${tab === "reports" ? "active" : ""}`} data-testid="pipeline-tab-reports" type="button" onClick={() => setTab("reports")}>
              Reports <em data-testid="pipeline-count-reports">{data?.report_count ?? "…"}</em>
            </button>
          </div>
          {tab === "reports" ? (
            <div className={`pipeline-layout ${current ? "with-detail" : ""}`}>
              <ReportsTab onOpen={(id) => { const row = (data?.items || []).find((item) => item.id === id); if (row) setSelected(row); }} />
              {current && <PipelineDetail row={current} clientOptions={clientOptions} onClose={() => setSelected(null)} onChanged={load} />}
            </div>
          ) : (
          <>
          <div className="pipeline-filters" data-testid="pipeline-filters">
            <label className="pipeline-search"><Search size={15} /><input data-testid="pipeline-search" placeholder="Search student, school, provider or service" value={q} onChange={(event) => setQ(event.target.value)} /></label>
            <select data-testid="pipeline-filter-client" value={filters.client_id} onChange={(event) => setFilters({ ...filters, client_id: event.target.value })}>
              <option value="">All schools</option>
              {(data?.filters?.clients || []).map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
            </select>
            <select data-testid="pipeline-filter-provider" value={filters.provider_id} onChange={(event) => setFilters({ ...filters, provider_id: event.target.value })}>
              <option value="">All providers</option>
              {(data?.filters?.providers || []).map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
            </select>
            <select data-testid="pipeline-filter-discipline" value={filters.discipline} onChange={(event) => setFilters({ ...filters, discipline: event.target.value })}>
              <option value="">All disciplines</option>
              {(data?.filters?.disciplines || []).map((discipline) => <option key={discipline} value={discipline}>{discipline}</option>)}
            </select>
          </div>
          <div className={`pipeline-layout ${current ? "with-detail" : ""}`}>
            <section className="pipeline-list" data-testid="pipeline-list">
              <header className="pipeline-row head" aria-hidden="true">
                <div>Student</div><div>Service · rate</div><div>Provider</div><div>Stage</div><div>Meeting</div><div>Due</div><div>Next step</div>
              </header>
              {!data && !error && <p className="projection-loading">Loading pipeline…</p>}
              {data && items.length === 0 && <p className="pipeline-empty" data-testid="pipeline-empty">Nothing here right now.</p>}
              {items.map((row) => <Row key={row.id} row={row} active={current?.id === row.id} onOpen={setSelected} />)}
            </section>
            {current && <PipelineDetail row={current} clientOptions={clientOptions} onClose={() => setSelected(null)} onChanged={load} />}
          </div>
          </>
          )}
        </main>
      </div>
    </div>
  );
}
